/**
 * Defers ResizeObserver callbacks into the next animation frame.
 *
 * Why: a portaled react-select menu (`menuPortalTarget`) is positioned by
 * @floating-ui's `autoUpdate`, which observes the menu portal and then writes
 * position and size styles back onto that same node from inside the observer
 * callback. When the option labels are wide enough that the write changes the
 * observed box, the browser cannot settle the notifications within one frame
 * and reports "ResizeObserver loop completed with undelivered notifications".
 * webpack-dev-server's overlay then renders that notice as a full-screen error.
 *
 * Deferring the callback moves the layout write out of the observation cycle,
 * so the loop cannot form. This is the same technique the hand-written observer
 * in HomeDashboard already uses. See `~/.claude-specs/incentco/ICOV3-1660-specs.md`.
 *
 * Consumers here only reposition or re-lay-out in response to a resize, so they
 * tolerate the one-frame delay; the trade-off is that a resize-driven layout
 * adjustment now lands one frame after paint rather than before it.
 *
 * ORDERING CONSTRAINT — do not "clean this up" into a top-of-file import.
 * It must be *called* from the body of `src/index.js`, not imported for its
 * side effects. Modules that construct a ResizeObserver at module scope (via
 * `resize-observer-polyfill`, which captures the class at import time) would
 * otherwise capture the deferred class instead of the native one, silently
 * widening this fix from "observers built at mount" to "every observer in the
 * app". Installing from the body keeps the shim scoped to observers that are
 * constructed lazily, which is every consumer we intend to affect.
 */
const installDeferredResizeObserver = () => {
  const NativeResizeObserver = window.ResizeObserver;

  if (!NativeResizeObserver) {
    return;
  }

  if (NativeResizeObserver.isDeferred) {
    return;
  }

  class DeferredResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      const deferredFrames = new Set();
      const deferredTargets = new Set();

      super((entries, observer) => {
        const frameId = window.requestAnimationFrame(() => {
          deferredFrames.delete(frameId);

          const observedEntries = entries.filter((entry) => deferredTargets.has(entry.target));

          if (!observedEntries.length) {
            return;
          }

          callback(observedEntries, observer);
        });

        deferredFrames.add(frameId);
      });

      // Deliberately distinct names: these must not shadow any field the native
      // ResizeObserver (or a test fake standing in for it) keeps on the instance.
      this.deferredFrames = deferredFrames;
      this.deferredTargets = deferredTargets;
    }

    observe(target, options) {
      this.deferredTargets.add(target);
      super.observe(target, options);
    }

    unobserve(target) {
      this.deferredTargets.delete(target);
      super.unobserve(target);
    }

    disconnect() {
      this.deferredFrames.forEach((frameId) => window.cancelAnimationFrame(frameId));
      this.deferredFrames.clear();
      this.deferredTargets.clear();
      super.disconnect();
    }
  }

  DeferredResizeObserver.isDeferred = true;
  window.ResizeObserver = DeferredResizeObserver;
};

export default installDeferredResizeObserver;
